/**
 * Routes pour le module Curriculum (Référentiels Pédagogiques)
 * Accessible uniquement aux administrateurs (et rôles autorisés)
 */

import { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '@/app/ProtectedRoute';
import { lazy } from 'react';

// Pages Dashboard
const CurriculumDashboardPage = lazy(
  () => import('@/pages/Curriculum/Dashboard/CurriculumDashboardPage')
);

// Pages Programs
const ProgramsListPage = lazy(
  () => import('@/pages/Curriculum/Programs/ProgramsListPage')
);
const ProgramCreatePage = lazy(
  () => import('@/pages/Curriculum/Programs/ProgramCreatePage')
);
const ProgramEditPage = lazy(
  () => import('@/pages/Curriculum/Programs/ProgramEditPage')
);
const ProgramDetailPage = lazy(
  () => import('@/pages/Curriculum/Programs/ProgramDetailPage')
);
const ProgramVersionDetailPage = lazy(
  () => import('@/pages/Curriculum/Programs/ProgramVersionDetailPage')
);

// Pages Competencies
const CompetenciesListPage = lazy(
  () => import('@/pages/Curriculum/Competencies/CompetenciesListPage')
);
const CompetencyCreatePage = lazy(
  () => import('@/pages/Curriculum/Competencies/CompetencyCreatePage')
);
const CompetencyEditPage = lazy(
  () => import('@/pages/Curriculum/Competencies/CompetencyEditPage')
);
const CompetencyDetailPage = lazy(
  () => import('@/pages/Curriculum/Competencies/CompetencyDetailPage')
);

// Pages Concepts
const ConceptsListPage = lazy(
  () => import('@/pages/Curriculum/Concepts/ConceptsListPage')
);
const ConceptCreatePage = lazy(
  () => import('@/pages/Curriculum/Concepts/ConceptCreatePage')
);
const ConceptEditPage = lazy(
  () => import('@/pages/Curriculum/Concepts/ConceptEditPage')
);
const ConceptDetailPage = lazy(
  () => import('@/pages/Curriculum/Concepts/ConceptDetailPage')
);

// Pages Subjects & Levels (Referentials)
const SubjectsListPage = lazy(
  () => import('@/pages/Curriculum/Subjects/SubjectsListPage')
);
const SubjectCreatePage = lazy(
  () => import('@/pages/Curriculum/Subjects/SubjectCreatePage')
);
const SubjectEditPage = lazy(
  () => import('@/pages/Curriculum/Subjects/SubjectEditPage')
);
const SubjectDetailPage = lazy(
  () => import('@/pages/Curriculum/Subjects/SubjectDetailPage')
);

// Pages Levels
const LevelsListPage = lazy(
  () => import('@/pages/Curriculum/Levels/LevelsListPage')
);
const LevelCreatePage = lazy(
  () => import('@/pages/Curriculum/Levels/LevelCreatePage')
);
const LevelEditPage = lazy(
  () => import('@/pages/Curriculum/Levels/LevelEditPage')
);
const LevelDetailPage = lazy(
  () => import('@/pages/Curriculum/Levels/LevelDetailPage')
);

// Pages SubjectTypes
const SubjectTypesListPage = lazy(
  () => import('@/pages/Curriculum/SubjectTypes/SubjectTypesListPage')
);
const SubjectTypeCreatePage = lazy(
  () => import('@/pages/Curriculum/SubjectTypes/SubjectTypeCreatePage')
);
const SubjectTypeEditPage = lazy(
  () => import('@/pages/Curriculum/SubjectTypes/SubjectTypeEditPage')
);
const SubjectTypeDetailPage = lazy(
  () => import('@/pages/Curriculum/SubjectTypes/SubjectTypeDetailPage')
);

// Pages DegreeTypes
const DegreeTypesListPage = lazy(
  () => import('@/pages/Curriculum/DegreeTypes/DegreeTypesListPage')
);

// Pages ClassSections
const ClassSectionsListPage = lazy(
  () => import('@/pages/Curriculum/ClassSections/ClassSectionsListPage')
);

export const curriculumRoutes: RouteObject[] = [
  {
    path: 'curriculum',
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <CurriculumDashboardPage />
          </ProtectedRoute>
        ),
      },
      // Programmes
      {
        path: 'programs',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <ProgramsListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute>
                <ProgramCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <ProgramDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute>
                <ProgramEditPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/versions/:versionId',
            element: (
              <ProtectedRoute>
                <ProgramVersionDetailPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Compétences
      {
        path: 'competencies',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <CompetenciesListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute>
                <CompetencyCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <CompetencyDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute>
                <CompetencyEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Notions
      {
        path: 'concepts',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <ConceptsListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute>
                <ConceptCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <ConceptDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute>
                <ConceptEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Matières & Niveaux
      {
        path: 'subjects',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <SubjectsListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute>
                <SubjectCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <SubjectDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute>
                <SubjectEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Niveaux
      {
        path: 'levels',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <LevelsListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute>
                <LevelCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <LevelDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute>
                <LevelEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Types de Matières
      {
        path: 'subject-types',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <SubjectTypesListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute>
                <SubjectTypeCreatePage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <ProtectedRoute>
                <SubjectTypeDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute>
                <SubjectTypeEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Types de Diplômes
      {
        path: 'degree-types',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <DegreeTypesListPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      // Sections de Classe
      {
        path: 'class-sections',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <ClassSectionsListPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
    ],
  },
];
